/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import React, {
    useEffect, useMemo, useRef, useState
} from 'react';
import {
    Search, Plus, AlertCircle, MoreHorizontal, Pencil, Trash2, Calendar,
    ChevronLeft, ChevronRight, X, Loader2
} from 'lucide-react';

import {
    DSButton, DSBadge, DSEmptyState
} from '../ui';

export const PAGE_SIZE_LIST = 20;
const PAGE_SIZE_GRID = 9;
const SERVER_PAGE_SIZE = PAGE_SIZE_LIST;
const CARD_MIN_HEIGHT = 140;

const TONE_ACCENT = {
    violet: '#B1092A',
    blue:   '#2461B0',
    green:  '#1F8E55',
    orange: '#B57108',
    rose:   '#C7384B',
};

function listClassName(vivid, withSteps = false) {
    const base = vivid ? 'ds-resource-list ds-resource-list--vivid' : 'ds-resource-list';
    return withSteps ? `${base} ds-resource-list--with-steps` : base;
}

/**
 * DSResourceList — unified list (Library) or card grid (Workforce).
 *
 * List mode: scannable rows — name, meta chips, updated, hover actions.
 * Grid mode: compact cards for narrative resources (assistants).
 *
 * Props:
 *   title       — section heading
 *   description — subtitle
 *   items       — array of resource records
 *   getKey      — (item) => string
 *   getCard     — (item) => { icon, tone, title, subtitle, description, badge,
 *                             stats, stepsLabel, idLine, createdBy, createdAt, searchTerms }
 *   onPick      — (item) => void   (card click)
 *   onEdit      — (item) => void   optional, in kebab menu
 *   onDelete    — (item) => void   optional, in kebab menu
 *   extraAction — { label, icon, onClick } optional extra kebab item (e.g. Provision)
 *   onCreate    — () => void       "+ New" CTA
 *   newLabel    — CTA label
 *   loading, error, searchPlaceholder, emptyTitle, emptyDescription, emptyIcon
 */
export default function DSResourceList({
    title,
    description,
    icon: SectionIcon,
    iconTone = 'violet',
    headerRight,
    items = [],
    getKey,
    getCard,
    onPick,
    onEdit,
    onDelete,
    extraAction,
    onCreate,
    newLabel = '+ New',
    creating = false,
    loading = false,
    error = null,
    searchPlaceholder = 'Search…',
    emptyTitle = 'Nothing here yet',
    emptyDescription,
    emptyIcon,
    // Server-side pagination — when provided, skip internal filter/sort/slice
    serverPage,
    serverTotalPages,
    onServerPage,
    serverItemCount,
    // Notifies the parent of the search term (debounced) so a server-paginated
    // list can fetch the full set and let us filter across ALL pages, not just
    // the current one. Opt-in: client-mode lists can ignore it.
    onSearchChange,
    variant = 'list',
    showStepsColumn = false,
}) {
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const listTopRef = useRef(null);
    const searchInputRef = useRef(null);

    const isServerMode = serverTotalPages !== undefined;

    // Debounced search → parent. Lets server-paginated lists switch to
    // fetch-all + client filter while typing, then back to paging when cleared.
    // Hold the callback in a ref + key the effect on `query` ONLY: the parent
    // passes a fresh inline onSearchChange every render, and depending on its
    // identity would re-fire this on unrelated re-renders (e.g. paging),
    // emitting an empty search that resets the parent's page back to 1.
    const onSearchChangeRef = useRef(onSearchChange);
    onSearchChangeRef.current = onSearchChange;
    const lastEmittedSearch = useRef('');
    useEffect(() => {
        const fn = onSearchChangeRef.current;
        if (!fn) return undefined;
        const next = query.trim();
        const t = setTimeout(() => {
            if (lastEmittedSearch.current === next) return; // no real change → don't reset paging
            lastEmittedSearch.current = next;
            fn(next);
        }, 300);
        return () => clearTimeout(t);
    }, [query]);

    const filtered = useMemo(() => {
        if (isServerMode) return items;
        const q = query.trim().toLowerCase();
        const result = q
            ? items.filter((it) => {
                const c = getCard(it) || {};
                return [c.title, c.subtitle, c.description, c.version, ...(c.searchTerms || [])]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                    .includes(q);
            })
            : [...items];
        // Newest first — sort by createdAt from getCard
        result.sort((a, b) => {
            const da = new Date(getCard(a)?.createdAt || 0).getTime();
            const db = new Date(getCard(b)?.createdAt || 0).getTime();
            return db - da;
        });
        return result;
    }, [items, query, getCard, isServerMode]);

    // Reset to page 1 whenever filter or items list changes
    useEffect(() => { if (!isServerMode) setPage(1); }, [query, items, isServerMode]);

    const isList = variant === 'list' || variant === 'list-vivid';
    const isVivid = variant === 'list-vivid';
    const pageSize = isList ? PAGE_SIZE_LIST : PAGE_SIZE_GRID;

    const totalPages = isServerMode ? serverTotalPages : Math.max(1, Math.ceil(filtered.length / pageSize));
    const activePage = isServerMode ? serverPage : page;
    const onPageChange = isServerMode ? onServerPage : setPage;
    const paged = isServerMode ? items : filtered.slice((page - 1) * pageSize, page * pageSize);
    const displayCount = isServerMode ? (serverItemCount ?? items.length) : filtered.length;

    function handlePageChange(nextPage) {
        onPageChange(nextPage);
        requestAnimationFrame(() => {
            listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }

    function clearSearch() {
        setQuery('');
        searchInputRef.current?.focus();
    }

    const headerAccent = TONE_ACCENT[iconTone] || TONE_ACCENT.violet;

    return (
        <div ref={listTopRef} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {/* Page-level header — icon box style when icon provided, simple bar otherwise */}
            {(title || description) && (
                SectionIcon ? (
                    <div style={{
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        marginBottom: 14,
                        paddingBottom: 14,
                        borderBottom: '1px solid var(--ds-border)',
                    }}>
                        <div style={{
                            width: 3,
                            alignSelf: 'stretch',
                            borderRadius: 2,
                            background: headerAccent,
                            flexShrink: 0,
                        }} />
                        <div style={{
                            width: 34, height: 34,
                            borderRadius: 'var(--ds-radius-sm)',
                            background: `${headerAccent}1a`,
                            color: headerAccent,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            <SectionIcon size={15} />
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{
                                fontSize: 'var(--ds-text-lg)',
                                fontWeight: 700,
                                color: 'var(--ds-text)',
                                letterSpacing: '-0.015em',
                                lineHeight: 1.2,
                            }}>{title}</div>
                            {description && (
                                <div style={{
                                    fontSize: 'var(--ds-text-xs)',
                                    color: 'var(--ds-text-muted)',
                                    marginTop: 3,
                                    letterSpacing: '0.01em',
                                }}>{description}</div>
                            )}
                        </div>
                        {headerRight && (
                            <div style={{ flexShrink: 0, marginLeft: 'auto' }}>
                                {headerRight}
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: 12,
                    }}>
                        <div style={{
                            width: 3,
                            borderRadius: 2,
                            background: 'var(--ds-primary)',
                            alignSelf: 'stretch',
                            marginRight: 14,
                            flexShrink: 0,
                            minHeight: 32,
                        }} />
                        <div>
                            {title && (
                                <h2 style={{
                                    margin: 0,
                                    fontSize: 'var(--ds-text-lg)',
                                    fontWeight: 700,
                                    color: 'var(--ds-text)',
                                    letterSpacing: '-0.015em',
                                }}>
                                    {title}
                                </h2>
                            )}
                            {description && (
                                <p style={{
                                    margin: '3px 0 0',
                                    fontSize: 'var(--ds-text-sm)',
                                    color: 'var(--ds-text-muted)',
                                    lineHeight: 1.5,
                                }}>
                                    {description}
                                </p>
                            )}
                        </div>
                    </div>
                )
            )}

            {/* Toolbar */}
            <div style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 12,
            }}>
                <div style={{
                    position: 'relative',
                    width: 480,
                    maxWidth: '100%',
                    flex: '1 1 480px',
                    minWidth: 240,
                }}>
                    <Search size={13} style={{
                        position: 'absolute',
                        right: query ? 32 : 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--ds-text-subtle)',
                        pointerEvents: 'none',
                    }} />
                    <input
                        ref={searchInputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={searchPlaceholder}
                        className="ds-input"
                        style={{
                            paddingLeft: 12,
                            paddingRight: query ? 56 : 32,
                            height: 32,
                            width: '100%',
                        }}
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={clearSearch}
                            className="ds-icon-btn"
                            aria-label="Clear search"
                            style={{
                                position: 'absolute',
                                right: 4,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 24,
                                height: 24,
                                color: 'var(--ds-text-muted)',
                            }}
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                <span style={{
                    fontSize: 'var(--ds-text-xs)',
                    color: 'var(--ds-text-subtle)',
                    flexShrink: 0,
                }}>
                    {displayCount} {displayCount === 1 ? 'item' : 'items'}
                </span>
                {onCreate && (
                    <DSButton
                        variant="primary"
                        size="sm"
                        onClick={onCreate}
                        disabled={creating}
                        style={{ flexShrink: 0, marginLeft: 'auto' }}
                    >
                        {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                        {creating ? 'Creating…' : newLabel}
                    </DSButton>
                )}
            </div>

            {error && (
                <div style={{
                    flexShrink: 0,
                    background: 'var(--ds-danger-soft)',
                    color: 'var(--ds-danger)',
                    border: '1px solid var(--ds-danger-soft)',
                    padding: '12px 16px',
                    borderRadius: 'var(--ds-radius)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    marginBottom: 16,
                }}>
                    <AlertCircle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
                    <div style={{ fontSize: 'var(--ds-text-sm)' }}>{error}</div>
                </div>
            )}

            {loading ? (
                isList ? (
                    <div className={listClassName(isVivid, showStepsColumn)}>
                        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                            <SkeletonRow key={i} vivid={isVivid} showStepsColumn={showStepsColumn} />
                        ))}
                    </div>
                ) : (
                    <div style={{
                        flex: 1,
                        display: 'grid',
                        gap: 16,
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gridAutoRows: `minmax(${CARD_MIN_HEIGHT}px, auto)`,
                        alignContent: 'start',
                    }}>
                        {[0, 1, 2, 3, 4, 5].map((i) => <SkeletonCard key={i} />)}
                    </div>
                )
            ) : paged.length === 0 ? (
                <DSEmptyState
                    icon={emptyIcon}
                    title={items.length === 0 ? emptyTitle : 'No matches'}
                    description={items.length === 0
                        ? emptyDescription
                        : `Nothing matches "${query}". Try a different search.`}
                    action={items.length === 0 && onCreate
                        ? <DSButton variant="primary" onClick={onCreate} disabled={creating}>
                            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            {creating ? 'Creating…' : newLabel}
                        </DSButton>
                        : null}
                />
            ) : (
                <>
                    {isList ? (
                        <div className={listClassName(isVivid, showStepsColumn)}>
                            <div className="ds-resource-list-head">
                                <span>#</span>
                                <span>Name</span>
                                {!showStepsColumn && <span>Details</span>}
                                {showStepsColumn && <span>Steps</span>}
                                <span>Description</span>
                                {!showStepsColumn && <span>Updated</span>}
                                {!showStepsColumn && <span>Version</span>}
                                <span />
                            </div>
                            {paged.map((it, idx) => (
                                <DSResourceRow
                                    key={getKey(it)}
                                    vivid={isVivid}
                                    showStepsColumn={showStepsColumn}
                                    index={(activePage - 1) * (isServerMode ? SERVER_PAGE_SIZE : pageSize) + idx + 1}
                                    card={getCard(it)}
                                    onClick={onPick ? () => onPick(it) : undefined}
                                    onEdit={onEdit ? () => onEdit(it) : undefined}
                                    onDelete={onDelete ? () => onDelete(it) : undefined}
                                    onExtra={extraAction ? () => extraAction.onClick(it) : undefined}
                                    extraLabel={extraAction?.label}
                                    extraIcon={extraAction?.icon}
                                />
                            ))}
                        </div>
                    ) : (
                        <div style={{
                            flex: 1,
                            display: 'grid',
                            gap: 16,
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gridAutoRows: `minmax(${CARD_MIN_HEIGHT}px, auto)`,
                            alignContent: 'start',
                        }}>
                            {paged.map((it) => (
                                <DSResourceCard
                                    key={getKey(it)}
                                    card={getCard(it)}
                                    onClick={onPick ? () => onPick(it) : undefined}
                                    onEdit={onEdit ? () => onEdit(it) : undefined}
                                    onDelete={onDelete ? () => onDelete(it) : undefined}
                                    onExtra={extraAction ? () => extraAction.onClick(it) : undefined}
                                    extraLabel={extraAction?.label}
                                    extraIcon={extraAction?.icon}
                                />
                            ))}
                        </div>
                    )}
                    {totalPages > 1 && (
                        <DSPagination page={activePage} totalPages={totalPages} onPage={handlePageChange} />
                    )}
                </>
            )}
        </div>
    );
}

/* ============================================================
 *                    Skeleton loading card
 * ============================================================ */

function SkeletonCard() {
    return (
        <div className="ds-card-flat ds-resource-card" style={{
            padding: '14px 16px',
            minHeight: CARD_MIN_HEIGHT,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <div className="animate-pulse" style={{ height: 13, borderRadius: 4, background: 'var(--ds-surface-3)', width: '45%', marginBottom: 8 }} />
            <div className="animate-pulse" style={{ height: 10, borderRadius: 4, background: 'var(--ds-surface-3)', width: '25%', marginBottom: 12 }} />
            <div className="animate-pulse" style={{ height: 11, borderRadius: 4, background: 'var(--ds-surface-3)', marginBottom: 5 }} />
            <div className="animate-pulse" style={{ height: 11, borderRadius: 4, background: 'var(--ds-surface-3)', width: '72%', marginBottom: 10 }} />
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                <div className="animate-pulse" style={{ height: 20, width: 56, borderRadius: 999, background: 'var(--ds-surface-3)' }} />
                <div className="animate-pulse" style={{ height: 20, width: 64, borderRadius: 999, background: 'var(--ds-surface-3)' }} />
            </div>
            <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 12, paddingTop: 10, borderTop: '1px solid var(--ds-divider)' }}>
                <div className="animate-pulse" style={{ height: 10, borderRadius: 4, background: 'var(--ds-surface-3)', width: 70 }} />
                <div className="animate-pulse" style={{ height: 10, borderRadius: 4, background: 'var(--ds-surface-3)', width: 90, marginLeft: 'auto' }} />
            </div>
        </div>
    );
}

function SkeletonRow({ vivid = false, showStepsColumn = false }) {
    return (
        <div className={vivid ? 'ds-resource-row ds-resource-row-skeleton ds-resource-row--vivid' : 'ds-resource-row-skeleton'}>
            <div className="animate-pulse" style={{ height: 10, borderRadius: 4, background: 'var(--ds-surface-3)', width: 16, margin: '0 auto' }} />
            <div className="animate-pulse" style={{ height: 12, borderRadius: 4, background: 'var(--ds-surface-3)', width: '70%' }} />
            {!showStepsColumn && (
                <div style={{ display: 'flex', gap: 6 }}>
                    <div className="animate-pulse" style={{ height: 20, width: 52, borderRadius: 4, background: 'var(--ds-surface-3)' }} />
                    <div className="animate-pulse" style={{ height: 20, width: 72, borderRadius: 4, background: 'var(--ds-surface-3)' }} />
                </div>
            )}
            {showStepsColumn && (
                <div className="animate-pulse" style={{ height: 11, borderRadius: 4, background: 'var(--ds-surface-3)', width: '80%' }} />
            )}
            <div className="animate-pulse" style={{ height: 11, borderRadius: 4, background: 'var(--ds-surface-3)', width: '85%' }} />
            {!showStepsColumn && (
                <>
                    <div className="animate-pulse" style={{ height: 10, borderRadius: 4, background: 'var(--ds-surface-3)', width: 56 }} />
                    <div className="animate-pulse" style={{ height: 10, borderRadius: 4, background: 'var(--ds-surface-3)', width: 32, margin: '0 auto' }} />
                </>
            )}
            <div />
        </div>
    );
}

/* ============================================================
 *                       List row
 * ============================================================ */

function fmtDateShort(raw) {
    if (!raw) return '—';
    try {
        const d = new Date(raw);
        if (isNaN(d.getTime())) return '—';
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
    } catch {
        return '—';
    }
}

function DSResourceRow({
    index, vivid = false, showStepsColumn = false, card, onClick, onEdit, onDelete, onExtra, extraLabel, extraIcon: ExtraIcon
}) {
    const {
        title,
        subtitle,
        description,
        version,
        badge,
        stats,
        stepsLabel,
        createdAt,
    } = card || {};

    const versionLabel = version
        || (badge?.label?.startsWith('v') ? badge.label : null)
        || null;

    const hasActions = onEdit || onDelete || onExtra;

    return (
        <div
            className={vivid ? 'ds-resource-row ds-resource-row--vivid' : 'ds-resource-row'}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            } : undefined}
        >
            <span className="ds-resource-row__index">{index}</span>

            <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="ds-resource-row__title" title={title}>{title}</span>
                {!vivid && badge && (
                    <DSBadge tone={badge.tone || 'neutral'} style={{ flexShrink: 0 }}>
                        {badge.label}
                    </DSBadge>
                )}
            </div>

            {!showStepsColumn && (
                <div className="ds-resource-row__meta">
                    {!vivid && subtitle && (
                        <span className="ds-resource-row__chip ds-resource-row__chip--muted" title={subtitle}>
                            {subtitle}
                        </span>
                    )}
                    {stats?.map((s, i) => (
                        <span
                            key={i}
                            className={[
                                'ds-resource-row__chip',
                                vivid && s.chipTone ? `ds-resource-row__chip--${s.chipTone}` : '',
                            ].filter(Boolean).join(' ')}
                            title={s.label}
                        >
                            {s.icon && <s.icon size={10} />}
                            {s.value !== undefined && <span style={{ fontWeight: 600 }}>{s.value}</span>}
                            {s.label}
                        </span>
                    ))}
                </div>
            )}

            {showStepsColumn && (
                <span className="ds-resource-row__steps" title={stepsLabel || undefined}>
                    {stepsLabel || '—'}
                </span>
            )}

            <span className="ds-resource-row__description" title={description || undefined}>
                {description || '—'}
            </span>

            {!showStepsColumn && (
                <span className="ds-resource-row__date">{fmtDateShort(createdAt)}</span>
            )}

            {!showStepsColumn && (
                <span className="ds-resource-row__version">{versionLabel || '—'}</span>
            )}

            {hasActions && (
                <div
                    className="ds-resource-row__actions"
                    onClick={(e) => e.stopPropagation()}
                >
                    {onEdit && (
                        <button
                            type="button"
                            className="ds-icon-btn"
                            title="Edit"
                            aria-label="Edit"
                            style={{ width: 28, height: 28 }}
                            onClick={() => onEdit()}
                        >
                            <Pencil size={13} />
                        </button>
                    )}
                    {onExtra && (
                        <button
                            type="button"
                            className="ds-icon-btn"
                            title={extraLabel || 'Action'}
                            aria-label={extraLabel || 'Action'}
                            style={{ width: 28, height: 28 }}
                            onClick={() => onExtra()}
                        >
                            {ExtraIcon ? <ExtraIcon size={13} /> : <MoreHorizontal size={13} />}
                        </button>
                    )}
                    {onDelete && (
                        <button
                            type="button"
                            className="ds-icon-btn"
                            title="Delete"
                            aria-label="Delete"
                            style={{ width: 28, height: 28, color: 'var(--ds-danger)' }}
                            onClick={() => onDelete()}
                        >
                            <Trash2 size={13} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

/* ============================================================
 *                      Pagination
 * ============================================================ */

function DSPagination({ page, totalPages, onPage }) {
    return (
        <div style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            marginTop: 10,
            paddingTop: 10,
            borderTop: '1px solid var(--ds-border)',
        }}>
            <button
                type="button"
                onClick={() => onPage(page - 1)}
                disabled={page === 1}
                className="ds-icon-btn"
                style={{ width: 30, height: 30, opacity: page === 1 ? 0.35 : 1, flexShrink: 0 }}
            >
                <ChevronLeft size={14} />
            </button>

            {/* Fixed-width center — never shifts layout when page number changes */}
            <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                width: 88,
                justifyContent: 'center',
                fontSize: 'var(--ds-text-xs)',
            }}>
                <span style={{
                    fontWeight: 700,
                    fontSize: 'var(--ds-text-sm)',
                    color: 'var(--ds-text)',
                    minWidth: 18,
                    textAlign: 'center',
                    transition: 'opacity 120ms ease',
                }}>
                    {page}
                </span>
                <span style={{ color: 'var(--ds-text-subtle)', fontSize: 11 }}>of</span>
                <span style={{
                    color: 'var(--ds-text-muted)',
                    minWidth: 18,
                    textAlign: 'center',
                }}>
                    {totalPages}
                </span>
            </div>

            <button
                type="button"
                onClick={() => onPage(page + 1)}
                disabled={page === totalPages}
                className="ds-icon-btn"
                style={{ width: 30, height: 30, opacity: page === totalPages ? 0.35 : 1, flexShrink: 0 }}
            >
                <ChevronRight size={14} />
            </button>
        </div>
    );
}

/* ============================================================
 *                       Card
 * ============================================================ */

function DSResourceCard({
    card, onClick, onEdit, onDelete, onExtra, extraLabel, extraIcon
}) {
    const {
        title, subtitle, description,
        badge,
        stats,
        createdAt,
    } = card || {};

    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const dateStr = fmtDateShort(createdAt);

    useEffect(() => {
        if (!menuOpen) return;
        function handler(e) {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        }
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [menuOpen]);

    return (
        <div
            className="ds-card-flat ds-resource-card"
            onClick={onClick}
            style={{
                padding: '14px 16px',
                cursor: onClick ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                minHeight: CARD_MIN_HEIGHT,
                boxSizing: 'border-box',
            }}
        >
            <div style={{ marginBottom: 8 }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0,
                }}>
                    <span style={{
                        fontWeight: 600,
                        fontSize: 'var(--ds-text-md)',
                        color: 'var(--ds-text)',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        letterSpacing: '-0.01em',
                    }} title={title}>{title}</span>
                    {badge && (
                        <DSBadge tone={badge.tone || 'neutral'} style={{ flexShrink: 0 }}>
                            {badge.label}
                        </DSBadge>
                    )}
                    <span style={{ flex: 1 }} />
                    {(onEdit || onDelete || onExtra) && (
                        <div ref={menuRef} style={{ flexShrink: 0, marginRight: -6, marginTop: -2 }}>
                            <KebabMenu
                                open={menuOpen}
                                setOpen={setMenuOpen}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                onExtra={onExtra}
                                extraLabel={extraLabel}
                                extraIcon={extraIcon}
                            />
                        </div>
                    )}
                </div>
                {subtitle && (
                    <div style={{
                        fontSize: 'var(--ds-text-xs)',
                        color: 'var(--ds-text-muted)',
                        marginTop: 3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                        {subtitle}
                    </div>
                )}
            </div>

            {description && (
                <div style={{
                    fontSize: 'var(--ds-text-sm)',
                    color: 'var(--ds-text-2)',
                    lineHeight: 1.45,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    marginBottom: 8,
                    flex: 1,
                }}>
                    {description}
                </div>
            )}

            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                paddingTop: 10,
                marginTop: 'auto',
                borderTop: '1px solid var(--ds-divider)',
                fontSize: 'var(--ds-text-xs)',
                color: 'var(--ds-text-subtle)',
            }}>
                {stats?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, flex: 1, minWidth: 0 }}>
                        {stats.map((s, i) => (
                            <span key={i} style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                background: 'var(--ds-surface-2)',
                                borderRadius: 4,
                                padding: '2px 8px',
                                fontSize: 'var(--ds-text-xs)',
                                color: 'var(--ds-text-muted)',
                            }}>
                                {s.icon && <s.icon size={10} />}
                                {s.value !== undefined && (
                                    <span style={{ fontWeight: 600 }}>{s.value}</span>
                                )}
                                <span>{s.label}</span>
                            </span>
                        ))}
                    </div>
                )}
                {dateStr && dateStr !== '—' && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                        <Calendar size={10} />
                        <span>{dateStr}</span>
                    </span>
                )}
            </div>
        </div>
    );
}

function KebabMenu({
    open, setOpen, onEdit, onDelete, onExtra, extraLabel, extraIcon: ExtraIcon
}) {
    return (
        <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                className="ds-icon-btn"
                aria-haspopup="menu"
                aria-expanded={open}
                style={{ width: 28, height: 28 }}
            >
                <MoreHorizontal size={14} />
            </button>
            {open && (
                <div
                    role="menu"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        right: 0,
                        minWidth: 148,
                        padding: 4,
                        background: 'var(--ds-surface)',
                        border: '1px solid var(--ds-border)',
                        borderRadius: 'var(--ds-radius)',
                        boxShadow: 'var(--ds-shadow-lg)',
                        zIndex: 20,
                        transformOrigin: 'top right',
                        animation: 'ds-dropdown-in 140ms var(--ds-ease)',
                    }}
                >
                    {onEdit && (
                        <button
                            type="button"
                            onClick={() => { setOpen(false); onEdit(); }}
                            className="ds-context-row"
                        >
                            <Pencil size={13} /> Edit
                        </button>
                    )}
                    {onExtra && (
                        <button
                            type="button"
                            onClick={() => { setOpen(false); onExtra(); }}
                            className="ds-context-row"
                        >
                            {ExtraIcon ? <ExtraIcon size={13} /> : null} {extraLabel || 'Action'}
                        </button>
                    )}
                    {onDelete && (
                        <button
                            type="button"
                            onClick={() => { setOpen(false); onDelete(); }}
                            className="ds-context-row"
                            style={{ color: 'var(--ds-danger)' }}
                        >
                            <Trash2 size={13} /> Delete
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
